<script setup lang="ts">
import { useThrottleFn } from '@vueuse/core'
import { ContactFormSchema } from '~~/shared/validation'

const toast = useToast()
const route = useRoute()
const { data: pageContent } = await useAsyncData(`content:pages${route.path}`, () => queryContent(`pages${route.path}`).findOne())

useSeoMeta({
  description: pageContent.value?.description,
  ogDescription: pageContent.value?.description,
  ogTitle: pageContent.value?.title,
  title: pageContent.value?.title,
})

 import.meta.prerender && defineOgImageComponent('TitleCard', {
  description: pageContent.value?.landing.description,
  title: pageContent.value?.landing.title,
})

const state = reactive({
  budget: undefined as string | undefined,
  email: undefined as string | undefined,
  name: undefined as string | undefined,
  phone: undefined as string | undefined,
  project: undefined as string | undefined,
})

const contactRequest = useFetch('/api/contact', {
  body: state,
  dedupe: 'defer',
  headers: {
    'Content-Type': 'application/json',
  },
  immediate: false,
  method: 'POST',
  server: false,
  watch: false,
})

const isSubmitting = computed(() => contactRequest.status.value == 'pending')
const isSuccessful = computed(() => contactRequest.status.value == 'success')
const shouldFormBeDisabled = computed(() => isSubmitting.value || isSuccessful.value)
const formError = computed(() => contactRequest.error.value?.statusMessage)
const shouldFocusForm = computed(() => route.hash === '#form')

watch(
  contactRequest.status,
  status => {
    if (status === 'success') {
      toast.add({
        color: 'primary',
        description: pageContent.value?.form.success.description,
        icon: pageContent.value?.form.success.icon,
        timeout: 8000,
        title: pageContent.value?.form.success.title,
      })
    } else if (status === 'error') {
      toast.add({
        color: 'red',
        description: pageContent.value?.form.error.description,
        icon: pageContent.value?.form.error.icon,
        timeout: 8000,
        title: pageContent.value?.form.error.title,
      })
    }
  },
)

const onRangeClick = useThrottleFn((range: string) => {
  if (state.budget === range) {
    state.budget = undefined
  } else {
    state.budget = range
  }
}, 300)

const onSubmit = useThrottleFn(() => {
  contactRequest.execute()
}, 300)
</script>

<template>
  <DotHeader v-bind="pageContent?.landing">
    <template #headline>
      <UButton
        v-if="pageContent?.landing.headline"
        v-bind="pageContent?.landing.headline"
      />
    </template>
  </DotHeader>

  <ULandingSection>
    <ULandingCTA
      id="form"
      :title="pageContent?.form.title"
      :description="pageContent?.form.description"
      color="primary"
      align="left"
      card
    >
      <UForm
        :schema="ContactFormSchema"
        :state="state"
        class="space-y-8"
        :validate-on="['submit']"
        @submit="onSubmit"
      >
        <UFormGroup
          v-for="field in ['name', 'email', 'phone'] as (keyof typeof state)[]"
          :key="field"
          :name="field"
        >
          <UInput
            v-model.trim="state[field]"
            :placeholder="pageContent?.form.fields[field].placeholder"
            :disabled="shouldFormBeDisabled"
            variant="none"
            class="border-b-2"
            :autofocus="shouldFocusForm && field === 'name'"
          />
        </UFormGroup>

        <UFormGroup :name="'project'">
          <UTextarea
            v-model.trim="state.project"
            :placeholder="pageContent?.form.fields.project.placeholder"
            :disabled="shouldFormBeDisabled"
            variant="none"
            autoresize
            :rows="1"
            class="border-b-2"
          />
        </UFormGroup>

        <UFormGroup :name="'budget'" class="px-2.5">
          <div class="block my-2 text-sm font-sm text-gray-400 dark:text-gray-500">{{ pageContent?.form.fields.budget.label }}</div>
          <div class="flex flex-wrap justify-start gap-2">
            <UButton
              v-for="range in pageContent?.form.fields.budget.ranges"
              :key="range"
              :label="range"
              :disabled="shouldFormBeDisabled"
              :active="state.budget === range"
              :variant="state.budget === range ? 'solid' : 'outline'"
              @click="() => onRangeClick(range)"
            />
          </div>
        </UFormGroup>

        <div v-if="formError" class="text-red-500 text-sm">
          {{ formError }}
        </div>

        <UButton
          v-if="!isSuccessful"
          type="submit"
          :label="isSubmitting? pageContent?.form.fields.submit.pending : pageContent?.form.fields.submit.label"
          :loading="isSubmitting"
          leading-icon="i-mdi-send"
        />
        <UAlert
          v-else
          :icon="pageContent?.form.success.icon"
          color="primary"
          variant="subtle"
          :title="pageContent?.form.success.title"
          :description="pageContent?.form.success.description"
        />
      </UForm>
    </ULandingCTA>
  </ULandingSection>
  <ConnectCard/>
</template>
